import { useNavigate } from 'react-router-dom';
import { GlassCard } from '@/components/ui/GlassCard';
import { StatsRing } from '@/components/ui/StatsRing';
import { TextureButton } from '@/components/ui/TextureButton';
import { Zap, Circle, Hammer, Trophy, Flame } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import type { TrainingSession } from '@/types';

/* ─── Athlete Header ─────────────────────────────────────────── */

interface AthleteHeaderProps {
    name: string;
    level?: string;
    avatar?: string;
}

import { motion } from 'framer-motion';

export function AthleteHeader({ name, level = 'Level 2 — Disciplined', avatar }: AthleteHeaderProps) {
    const navigate = useNavigate();

    // Fallback for visual impact if name is generic or empty
    const displayName = name === 'Wrestler' || !name ? "DEMO WRESTLER" : name;

    return (
        <div className="flex items-center justify-between px-6 py-6 relative z-10 w-full max-w-lg mx-auto">
            <div className="flex flex-col flex-1 min-w-0 pr-4">
                <motion.h1
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="text-[32px] font-black text-transparent bg-clip-text bg-gradient-to-br from-white via-white to-white/60 leading-none uppercase tracking-tighter drop-shadow-sm truncate"
                >
                    {displayName}
                </motion.h1>

                <motion.div
                    initial={{ opacity: 0, scale: 0.9, originX: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2, duration: 0.4 }}
                    className="inline-flex items-center gap-2 mt-2"
                >
                    <div className="h-px w-8 bg-gold-500/50" />
                    <span className="text-[11px] font-extrabold uppercase tracking-widest text-gold-500 shadow-gold-glow">
                        {level}
                    </span>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, type: "spring" }}
                className="flex items-center gap-4"
            >
                <button
                    onClick={() => navigate('/profile')}
                    className="w-14 h-14 rounded-full bg-bg-surface2 border-2 border-white/10 flex items-center justify-center hover:scale-105 transition-transform active:scale-95 cursor-pointer relative overflow-hidden group shadow-2xl"
                >
                    {avatar ? (
                        avatar.startsWith('http') || avatar.startsWith('/') || avatar.startsWith('data:') ? (
                            <img src={avatar} alt={name} className="w-full h-full object-cover" />
                        ) : (
                            <span className="text-2xl pt-1 select-none grayscale group-hover:grayscale-0 transition-all">{avatar}</span>
                        )
                    ) : (
                        <span className="text-lg font-black text-text-secondary group-hover:text-white transition-colors">
                            {displayName.charAt(0)}
                        </span>
                    )}

                    {/* Ring highlight */}
                    <div className="absolute inset-0 rounded-full border border-white/10 group-hover:border-gold-500/50 transition-colors" />
                </button>
            </motion.div>
        </div>
    );
}

/* ─── Performance Snapshot ───────────────────────────────────── */

interface PerformanceSnapshotProps {
    weeklyMinutes: number;
    streak: number;
    masteredCount: number;
    totalSkills: number;
}

export function PerformanceSnapshot({ weeklyMinutes, streak, masteredCount, totalSkills }: PerformanceSnapshotProps) {
    const navigate = useNavigate();
    // Goal: 120 mins/week
    const weeklyGoal = 120;
    const weeklyProgress = Math.min(100, Math.round((weeklyMinutes / weeklyGoal) * 100));

    const masteryProgress = totalSkills === 0 ? 0 : Math.min(100, Math.round((masteredCount / totalSkills) * 100));

    return (
        <div className="px-6 animate-fade-in-up stagger-1 relative z-10 w-full max-w-lg mx-auto space-y-3">
            <div className="grid grid-cols-3 gap-3">
                <GlassCard className="h-[120px] p-0 flex flex-col items-center justify-center gap-2">
                    <div className="scale-75 origin-center">
                        <StatsRing percentage={weeklyProgress} color="#FFD700" />
                    </div>
                    <div className="text-center -mt-1">
                        <span className="text-[20px] font-black text-white leading-none block">
                            {weeklyMinutes}
                        </span>
                        <span className="text-[9px] font-bold uppercase tracking-wider text-text-muted">
                            Wk Mins
                        </span>
                    </div>
                </GlassCard>

                <GlassCard className="h-[120px] p-4 flex flex-col items-center justify-center gap-1 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-gold-500/10 to-transparent opacity-50" />
                    <div className="text-[42px] font-black text-white leading-none tracking-tighter drop-shadow-gold">
                        {streak}
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gold-500/10 border border-gold-500/20">
                        <Flame className="w-3.5 h-3.5 text-orange-500 fill-orange-500 animate-pulse" />
                        <span className="text-[9px] font-bold uppercase tracking-widest text-gold-500">
                            Streak
                        </span>
                    </div>
                </GlassCard>

                <GlassCard className="h-[120px] p-0 flex flex-col items-center justify-center gap-2">
                    <div className="scale-75 origin-center">
                        <StatsRing percentage={masteryProgress} color="#22d3ee" />
                    </div>
                    <div className="text-center -mt-1">
                        <span className="text-[20px] font-black text-white leading-none block">
                            {masteredCount}/{totalSkills}
                        </span>
                        <span className="text-[9px] font-bold uppercase tracking-wider text-text-muted">
                            Mastery
                        </span>
                    </div>
                </GlassCard>
            </div>

            {/* Link to full analytics */}
            <button
                onClick={() => navigate('/analytics')}
                className="w-full text-center text-[10px] font-bold text-text-muted uppercase tracking-widest py-1.5 hover:text-gold-400 transition-colors"
            >
                View All Stats →
            </button>
        </div>
    );
}

/* ─── Focus Section ──────────────────────────────────────────── */

export function FocusSection() {
    return (
        <div className="animate-fade-in-up stagger-3 space-y-4 px-6 pb-24">
            <div className="flex items-center justify-between">
                <h3 className="text-[13px] font-bold text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold-500" />
                    Current Focus
                </h3>
            </div>

            <div className="grid gap-4">
                <GlassCard className="relative aspect-video flex items-end p-5 overflow-hidden group cursor-pointer hover:border-gold/30 transition-colors">
                    {/* Background Image Placeholder */}
                    <div className="absolute inset-0 bg-neutral-800">
                        {/* Replace with real image */}
                        <div className="w-full h-full bg-gradient-to-br from-neutral-800 to-neutral-900 opacity-50" />
                    </div>

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

                    <div className="relative z-10">
                        <span className="text-[10px] font-bold text-gold-400 uppercase tracking-widest mb-1 block">
                            Fundamentals
                        </span>
                        <h4 className="text-[20px] font-black text-white leading-tight uppercase tracking-tight group-hover:text-gold-100 transition-colors">
                            Ankle Pick Setups
                        </h4>
                    </div>
                </GlassCard>

                <GlassCard className="relative aspect-video flex items-end p-5 overflow-hidden group cursor-pointer hover:border-gold/30 transition-colors">
                    {/* Background Image Placeholder */}
                    <div className="absolute inset-0 bg-neutral-800">
                        <div className="w-full h-full bg-gradient-to-br from-blue-900/20 to-purple-900/20 opacity-50" />
                    </div>

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

                    <div className="relative z-10">
                        <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-widest mb-1 block">
                            Advanced Flow
                        </span>
                        <h4 className="text-[20px] font-black text-white leading-tight uppercase tracking-tight group-hover:text-cyan-100 transition-colors">
                            Chain Wrestling
                        </h4>
                    </div>
                </GlassCard>
            </div>
        </div>
    );
}

/* ─── Hero Action ────────────────────────────────────────────── */

export function HeroAction() {
    const navigate = useNavigate();

    return (
        <div className="px-6 animate-fade-in-up stagger-2 py-4 relative z-10 w-full max-w-lg mx-auto">
            <TextureButton onClick={() => navigate('/train')}>
                <span className="text-[11px] font-black uppercase tracking-[0.3em] text-black/60 mb-2 block">
                    Ready to Work
                </span>
                <h2 className="text-[32px] sm:text-[40px] font-black text-black leading-none tracking-tighter drop-shadow-sm flex items-center justify-center gap-3">
                    ENTER SESSION
                </h2>

                <div className="mt-4 flex items-center justify-center gap-2 px-4 py-1.5 rounded-full bg-black/10 backdrop-blur-sm border border-black/5 group-hover:bg-black/20 transition-colors mx-auto w-fit">
                    <Zap className="w-3.5 h-3.5 text-black/70 fill-current animate-pulse" />
                    <span className="text-[11px] font-bold uppercase tracking-wider text-black/70">
                        Quick Start
                    </span>
                </div>
            </TextureButton>
        </div>
    );
}

/* ─── Skill Momentum ─────────────────────────────────────────── */

interface SkillMomentumProps {
    activeSkills: Array<{ name: string; progress: number }>;
}

export function SkillMomentum({ activeSkills }: SkillMomentumProps) {
    if (activeSkills.length === 0) return null;

    return (
        <div className="animate-fade-in-up stagger-3 space-y-4 pt-2">
            <div className="px-6 flex items-center justify-between">
                <h3 className="text-[13px] font-bold text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold-500" />
                    Skill Momentum
                </h3>
                <span className="text-[10px] font-bold text-gold-500 uppercase tracking-wider">View All</span>
            </div>

            {/* Horizontal Scroll Container */}
            <div className="flex gap-4 overflow-x-auto px-6 pb-4 -mx-0 snap-x hide-scrollbar">
                {activeSkills.map((ski, i) => (
                    <div
                        key={i}
                        className="flex-shrink-0 w-[150px] h-[130px] rounded-[24px] bg-bg-surface border border-white/[0.05] p-5 flex flex-col items-center justify-between snap-start relative overflow-hidden group"
                    >
                        {/* Subtle bg gradient */}
                        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                        <StatsRing
                            percentage={ski.progress}
                            color={ski.progress === 100 ? '#F4D675' : '#3B82F6'}
                        />

                        <div className="text-center z-10">
                            <span className="text-[12px] font-bold text-text-primary leading-tight line-clamp-1 block mb-0.5">
                                {ski.name}
                            </span>
                            <span className={cn(
                                "text-[10px] font-bold uppercase tracking-wider",
                                ski.progress === 100 ? "text-gold-500" : "text-text-muted"
                            )}>
                                {ski.progress}% MSTR
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ─── Recent Sessions ────────────────────────────────────────── */

interface RecentSessionsProps {
    sessions: TrainingSession[];
}

export function RecentSessions({ sessions }: RecentSessionsProps) {
    if (sessions.length === 0) return null;

    return (
        <div className="animate-fade-in-up stagger-4 space-y-4 px-6 pb-24">
            <h3 className="text-[13px] font-bold text-white uppercase tracking-wider flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-white/20" />
                Recent Activity
            </h3>

            <div className="space-y-3">
                {sessions.map((session) => (
                    <div key={session.id} className="relative overflow-hidden rounded-[20px] bg-bg-surface border border-white/[0.05] p-1 group">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.02] to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />

                        <div className="flex items-center justify-between p-3 bg-bg-surface2/50 rounded-[16px]">
                            <div className="flex items-center gap-4">
                                <div className={cn(
                                    "w-12 h-12 rounded-2xl flex items-center justify-center border shadow-inner",
                                    session.position === 'neutral' ? "bg-blue-500/10 border-blue-500/20 text-blue-400" :
                                        session.position === 'top' ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400" :
                                            "bg-rose-500/10 border-rose-500/20 text-rose-400"
                                )}>
                                    {session.position === 'neutral' && <Circle size={20} className="fill-current opacity-50" />}
                                    {session.position === 'top' && <Hammer size={20} />}
                                    {session.position === 'bottom' && <Trophy size={20} />}
                                </div>
                                <div>
                                    <h4 className="text-[15px] font-bold text-text-primary">
                                        {session.presetId ? 'Preset Drill' : `${session.position.charAt(0).toUpperCase() + session.position.slice(1)} Game`}
                                    </h4>
                                    <p className="text-[11px] text-text-muted font-bold uppercase tracking-wide opacity-60">
                                        {format(new Date(session.startedAt), 'MMM d • h:mm a')}
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-col items-end pr-2">
                                <span className="text-[18px] font-black text-white tabular-nums leading-none">
                                    {Math.round(session.totalSeconds / 60)}
                                </span>
                                <span className="text-[9px] font-bold text-text-muted uppercase tracking-wider">
                                    MIN
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ─── AI Analysis (Protected) ────────────────────────────────── */

import { GuestGate } from '@/components/ui/GuestGate';

export function AIAnalysisSection() {
    return (
        <div className="animate-fade-in-up stagger-3 space-y-4 px-6 pb-2">
            <h3 className="text-[13px] font-bold text-white uppercase tracking-wider flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                AI Analysis
            </h3>

            <GuestGate>
                <GlassCard className="relative p-5 overflow-hidden group cursor-pointer hover:border-purple-500/30 transition-colors">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-900/10 to-transparent" />
                    <div className="relative z-10 flex items-center justify-between">
                        <div>
                            <span className="text-[10px] font-bold text-purple-400 uppercase tracking-widest mb-1 block">
                                Match Insight
                            </span>
                            <h4 className="text-[18px] font-black text-white leading-tight uppercase tracking-tight">
                                Stance Efficiency
                            </h4>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center border border-purple-500/30">
                            <Zap className="w-5 h-5 text-purple-400 fill-purple-400/50" />
                        </div>
                    </div>
                </GlassCard>
            </GuestGate>
        </div>
    );
}
